import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// Serializer
import LabOrderInterfaceSerializer from "issara-sdk/types/CentralLabOrderListSerializer_apps_LAB";

// API
import LabOrderList from "issara-sdk/apis/CentralLabOrderList_apps_LAB";
import LabOrderTriggerView from "issara-sdk/apis/CentralLabOrderTriggerView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";
import DivisionList from "issara-sdk/apis/DivisionList_core";

export type Props = {

}

export type State = Partial<{
  // seq
  LabOrderInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    LabOrderInterfaceList: LabOrderInterfaceSerializer[];
    selectedItems: any[];
    searchLabOrder: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type FilterSearchType = Partial<{
  hn: string | null;
}>;

type PaginatedItemsType = Partial<{
  items: LabOrderInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "LabOrderInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "TRIGGER";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof LabOrderInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof LabOrderInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  LabOrderInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  errorMessage: null,
  successMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardLabOrderInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  hn: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      LabOrderInterfaceSequence: {
        ...state.LabOrderInterfaceSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    // * issue #74787
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchLabOrder = state.LabOrderInterfaceSequence?.searchLabOrder;
  const paginatedItems = state.LabOrderInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  // * issue 75104
  if (!searchLabOrder?.hn) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: "กรุณาระบุ HN ก่อนทำรายการ",
      },
    });

    return;
  }

  let data = {
    limit: pageSize,
    offset,
    hn: searchLabOrder?.hn,
  };

  const result = await GetLabOrderInterfaceData(controller, data);

  if (result?.items) {

    controller.setState({
      LabOrderInterfaceSequence: {
        ...state.LabOrderInterfaceSequence,
        LabOrderInterfaceList: result?.items,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveLabOrderInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  //! Response ตอน Trigger แล้วได้ "" ทำให้ Skip Condition เลยแสดง Button Status ไม่ถูก
  if (saveResp || saveResp === "") {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      }
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    LabOrderInterfaceSequence: {
      ...state.LabOrderInterfaceSequence,
      searchLabOrder: { ...FILTER_INIT },
      LabOrderInterfaceList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetLabOrderInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  /* params: 'hn' */
  const [resp, err] = await LabOrderList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: {
      pk: params.id,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const GetDivisionList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DivisionList.list({
    apiToken: controller.apiToken,
    params: {
      limit: 9999,
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items;
};

const SaveLabOrderInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await LabOrderTriggerView.put({
    apiToken: controller.apiToken,
    data: {
      central_lab_orders: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

/**========================================================================
 **                               Utils
 *========================================================================**/

export const accessorList: Partial<Record<keyof LabOrderInterfaceSerializer, string>> = {
  id: "ID",
  specific_type: "Specific Type",
  edited_doctor: "Editted Doctor",
  doctor_edit_time: "Doctor Edit Time",
  status_name: "Order Status",
  // order_status: "Order Status",
  payment_status_name: "Order Payment Status",
  // order_payment_status: "Order Payment Status",
  order_name: "Order Name",
  order_type: "Order Type",
  order_by: "Order By",
  // order_div: "Order Div",
  order_time: "Order Time",
  order_receive_by: "Order Receive By",
  order_receive_time: "Order Receive Time",
  order_approve_time: "Order Approve Time",
  order_perform_div: "Order Perform Div",
  order_perform_by: "Order Perform By",
  order_perform_time: "Order Perform Time",
  order_ack_by: "Order Ack By",
  order_ack_time: "Order Ack Time",
  order_ack_div: "Order Ack Div",
  // is_advance: any,
  // is_out_perform_div: any,
  out_perform_div: "Out Perform Div",
  progression_cycle: "Progression Cycle",
  // emr: "EMR",
  // encounter: "Encounter",
  // is_seen: any,
  // doctororder_ptr: "Doctor Order PTR",
  edit_user: "Edit User",
  created: "Created",
  edited: "Edited",
  // status?: any,
  // full_access_number: "Full Access Number",
  highest_urgency: "Highest Urgency",
  // lab_speciality?: any,
  // pk?: any,
  // action: "Action",
  // order_items?: any,
  // specimen_containers?: any,
  date: "Date",
  hn: "HN",
  patient_name: "Patient Name",
  doctor_name: "Doctor Name",
  division_name: "Division",
  encounter_number: "Encounter Number",
  encounter_type: "Encounter Type",
  order_div_name: "Order Div",
  arrive_time: "Arrive Time",
  // payment_status_label: "Payment Status",
  note: "Note",
  // allow_duplicate_flag: "Allow Duplicate Flag",
  // ack_unavailable_lab: "Ack Unavailable Lab",
  order_summary: "Order Summary",
  lab_speciality_display_name: "Lab Speciality",
  // order_list?: any,
  nurse_status: "Nurse Status",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<LabOrderInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof LabOrderInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};